import { css } from "@emotion/css";
import { parseDate } from "@internationalized/date";
import { format, parseISO } from "date-fns";
import { DateValue } from "react-aria";

import { DateSelectValue } from "./DateSelector";
import { Granularity } from "./constants";

export function formatDateSelectValueAsISODate(value?: DateSelectValue) {
  if (!value) {
    return null;
  }
  const { year, month, day } = value;
  if (!year || !month || !day) return "";

  return format(new Date(year, month - 1, day), "yyyy-MM-dd").toString();
}

export function parseISODateAsDateSelectValue(
  value: string
): DateSelectValue | null {
  try {
    const isoDate = parseISO(value);

    return {
      year: isoDate.getFullYear() || 0,
      month: isoDate.getMonth() + 1 || 0,
      day: isoDate.getDate() || 0,
    };
  } catch {
    return null;
  }
}

export function validateDateSegments(
  t: (str: string) => string,
  date: DateSelectValue | undefined,
  label: string,
  granularity?: Granularity
) {
  const invalidSegments = [];
  const isDayHidden = granularity === Granularity.month;

  if (!isDayHidden && !date?.day) {
    invalidSegments.push(t("day"));
  }

  if (!date?.month) {
    invalidSegments.push(t("month"));
  }

  if (!date?.year) {
    invalidSegments.push(t("year"));
  }

  if (invalidSegments.length === 1) {
    return `${label}: ${invalidSegments[0]} is required`;
  }

  if (invalidSegments.length > 1) {
    const lastSegment = invalidSegments.slice(-1);
    const firstSegments = invalidSegments.slice(0, -1).join(", ");
    const joinedSegments = [firstSegments, lastSegment].join(`, ${t("and")} `);
    return `${label}: ${joinedSegments} ${t("are required")}`;
  }
}

export function parseDateFromValue(
  data: string | Record<string, string> | undefined,
  defaultValue: DateValue | null = null
) {
  if (!data) {
    return defaultValue;
  }

  try {
    if (typeof data === "object") {
      return {
        start: parseDate(data.start),
        end: parseDate(data.end),
      };
    }
    return parseDate(data);
  } catch {
    return defaultValue;
  }
}

export function useDatePickerStyles() {
  return {
    dateSegment: css({
      color: "var(--text-placeholder)",
      caretColor: "transparent",
      borderRadius: "var(--border-radius-2)",
      outline: "none",
      "&[data-focused]": {
        backgroundColor: "var(--background-accent-tofino-strong)",
        color: "var(--text-inverse)",
      },
    }),
    toggle: css({
      all: "unset",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "var(--space-6)",
      height: "var(--space-6)",
      borderRadius: "var(--border-radius-2)",
      "&[data-focused]": {
        backgroundColor: "var(--background-accent-tofino-strong)",
        "> i": {
          color: "var(--text-inverse)",
        },
      },
    }),
    popover: css({ overflowY: "auto" }),
    calendar: css({
      width: "254px",
      display: "flex",
      flexDirection: "column",
      padding: `var(--space-7) var(--space-8)`,
    }),
    header: css({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      height: "var(--space-9)",
      marginBottom: "var(--space-1)",
    }),
    heading: css({
      fontSize: "var(--font-size-200)",
      fontWeight: "var(--font-weight-medium)",
    }),
    calendarGrid: css({ gap: "var(--space-2)" }),
    headerCell: css({
      width: "var(--font-size-500)",
      height: "var(--font-size-500)",
      fontSize: "var(--font-size-75)",
    }),
    input: css({
      paddingLeft: "var(--space-05)",
      paddingRight: "var(--space-05)",
    }),
    calendarCell: css({
      margin: "auto",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "var(--space-8)",
      height: "var(--space-8)",
      padding: "0px",
      borderRadius: "var(--border-radius-2)",
      cursor: "pointer",
      outline: "none",

      "&[aria-disabled=true]": {
        color: "var(--text-disabled)",
      },

      "&[data-outside-month]": {
        display: "none",
      },

      "&[data-hovered], &[data-focused]": {
        backgroundColor: "var(--background-accent-neutral-medium)",
      },

      "&[data-selected]": {
        backgroundColor: "var(--background-accent-tofino-strong)",
        color: "var(--text-inverse)",
      },
    }),
  };
}

export function getDisplayNames(locale: string) {
  try {
    return new Intl.DisplayNames(locale, { type: "dateTimeField" });
  } catch (error) {
    console.warn(
      "dateTimeField is not supported by Intl.DisplayNames in this environment.",
      error
    );
    return undefined;
  }
}
